/**
 * Pages component - Horizontal flex with 3 columns:
 * - Icon (24x24, dark grey)
 * - Page label (with slug prefix rule)
 * - Chevron (points up)
 */

import { createChevron, type ChevronAPI } from "@UI/Chevron/index.js";
import { createIcon, type IconAPI } from "@UI/Icon/index.js";
import "./Pages.scss"; // Style co-located with component [[memory:8094583]]

export type PageIconName =
  | "page_home"
  | "page_slug"
  | "tools_component"
  | "modes_sketch";

export interface PagesConfig {
  readonly label?: string;
  readonly icon?: PageIconName;
  readonly onClick?: () => void; // click on the whole container
  readonly onChevronClick?: (anchorEl: HTMLElement) => void;
}

export interface PagesAPI {
  readonly getElement: () => HTMLElement;
  readonly setLabel: (label: string) => void;
  readonly setIcon: (icon: PageIconName) => void;
  readonly destroy: () => void;
}

export const createPages = (config: PagesConfig = {}): PagesAPI => {
  const {
    label = "Home",
    icon = "page_home",
    onClick,
    onChevronClick,
  } = config;

  let isDestroyed = false;
  let currentLabel: string = label;
  let currentIcon: PageIconName = icon;
  let chevronAPI: ChevronAPI | null = null;
  let iconAPI: IconAPI | null = null;

  // Root container
  const container = document.createElement("div");
  container.className = "pages";
  if (onClick) {
    container.style.cursor = "pointer";
    container.addEventListener("click", onClick);
  } else if (onChevronClick) {
    // Make whole component a touch target that opens the same modal anchored to the chevron
    container.style.cursor = "pointer";
    container.addEventListener("click", () => {
      const anchor = chevronAPI?.getElement() ?? chevronEl;
      onChevronClick(anchor);
    });
  }

  // Icon element
  const iconEl = document.createElement("div");
  iconEl.className = "pages__icon";

  const attachIcon = (): void => {
    iconEl.innerHTML = "";
    iconAPI?.destroy();
    iconAPI = createIcon({
      name: currentIcon,
      size: 20,
      color: "var(--color-medium-grey)",
      useContainer: true,
    });
    iconEl.appendChild(iconAPI.getElement());
  };

  // Name element
  const nameEl = document.createElement("div");
  nameEl.className = "pages__name";

  // Chevron container
  const chevronEl = document.createElement("div");
  chevronEl.className = "pages__chevron";

  const attachChevron = (): void => {
    chevronAPI?.destroy();
    chevronAPI = createChevron({
      orientation: "up",
      className: "pages__chevron-container",
      svgClassName: "pages__chevron-svg",
      ...(onChevronClick
        ? {
            onClick: () => {
              const anchor = chevronAPI?.getElement() ?? chevronEl;
              onChevronClick(anchor);
            },
          }
        : {}),
    });
    chevronEl.appendChild(chevronAPI.getElement());
  };

  const computeDisplayLabel = (raw: string, iconName: PageIconName): string => {
    const needsPrefix = iconName === "page_slug";
    const trimmed = raw.trim();
    if (!needsPrefix) return trimmed;
    const normalized = trimmed.replace(/^\/?\s*/, "");
    return `/ ${normalized}`;
  };

  const updateLabel = (): void => {
    nameEl.textContent = computeDisplayLabel(currentLabel, currentIcon);
  };

  const render = (): void => {
    container.innerHTML = "";

    // Layout: [icon] [name] [chevron]
    attachIcon();
    container.appendChild(iconEl);
    updateLabel();
    container.appendChild(nameEl);
    attachChevron();
    container.appendChild(chevronEl);
  };

  const setLabel = (newLabel: string): void => {
    if (isDestroyed) return;
    currentLabel = newLabel;
    updateLabel();
  };

  const setIcon = (newIcon: PageIconName): void => {
    if (isDestroyed) return;
    currentIcon = newIcon;
    attachIcon();
    updateLabel();
  };

  const destroy = (): void => {
    if (isDestroyed) return;
    isDestroyed = true;
    chevronAPI?.destroy();
    chevronAPI = null;
    iconAPI?.destroy();
    iconAPI = null;
    if (onClick) {
      container.removeEventListener("click", onClick);
    }
    container.remove();
  };

  // Init
  render();

  return {
    getElement: () => container,
    setLabel,
    setIcon,
    destroy,
  };
};

export const createPagesInContainer = (
  container: HTMLElement,
  config: PagesConfig = {}
): PagesAPI => {
  const api = createPages(config);
  container.appendChild(api.getElement());
  return api;
};
